import React, { useEffect } from 'react';
import { useMultilingualSEO } from '../../hooks/useMultilingualSEO';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import { useTranslation } from 'react-i18next';

interface MultilingualSEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  hreflang?: boolean;
  ogImage?: string;
  twitterCard?: string;
}

export const MultilingualSEO: React.FC<MultilingualSEOProps> = (props) => {
  useMultilingualSEO(props as any);
  return null; // Ce composant ne rend rien, il gère juste le SEO
};

// Hook pour générer les URLs alternatives
export const useAlternateURLs = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  return Object.entries(SUPPORTED_LANGUAGES).map(([lang, config]) => ({
    lang,
    url: `https://komby.io${config.path || ''}${window.location.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?/, '') || ''}`,
    name: config.name,
    flag: config.flag,
    currentLanguage
  }));
};

// Ajoute dynamiquement les <link rel="alternate" hreflang="..."> et la canonique
export const HreflangTags: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const head = document.head;

    // Nettoyer les précédents tags injectés
    head.querySelectorAll('link[data-dynamic-hreflang], meta[data-dynamic-og]').forEach((el) => el.parentElement?.removeChild(el));

    const pathSuffix = window.location.pathname.replace(/^\/[a-z]{2}(?:-[A-Z]{2})?/, '') || '/';
    // Ensure trailing slash for URL consistency
    const normalizedPath = pathSuffix.endsWith('/') || pathSuffix === '/' ? pathSuffix : `${pathSuffix}/`;
    const isHome = normalizedPath === '/';

    // Définir lang sur <html>
    document.documentElement.setAttribute('lang', i18n.language || 'en');

    // Self-canonical per URL (hreflang handles language alternates).
    // Canonical→base locale was ignored by Google → GSC "Duplicate without user-selected canonical".
    const currentLang = i18n.language;
    const currentLangConfig = SUPPORTED_LANGUAGES[currentLang as keyof typeof SUPPORTED_LANGUAGES];

    // EN home = https://komby.io/ (Google-chosen canonical; /en/ 301 → /)
    const langHomeUrl = (lang: string, configPath: string) =>
      lang === 'en' && isHome
        ? 'https://komby.io/'
        : `https://komby.io${configPath}${normalizedPath}`;

    const canonicalUrl = currentLangConfig
      ? langHomeUrl(currentLang, currentLangConfig.path)
      : `${window.location.origin}${window.location.pathname.endsWith('/') ? window.location.pathname : `${window.location.pathname}/`}`;

    const canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', canonicalUrl);
    canonical.setAttribute('data-dynamic-hreflang', 'true');
    head.appendChild(canonical);

    // x-default → EN home at / ; other pages → /en/...
    const enConfig = SUPPORTED_LANGUAGES['en' as keyof typeof SUPPORTED_LANGUAGES];
    const xDefaultUrl = enConfig
      ? langHomeUrl('en', enConfig.path)
      : (isHome ? 'https://komby.io/' : `https://komby.io/en${normalizedPath}`);
    
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', xDefaultUrl);
    xDefault.setAttribute('data-dynamic-hreflang', 'true');
    head.appendChild(xDefault);

    // Pour chaque langue supportée (with trailing slash for consistency)
    Object.entries(SUPPORTED_LANGUAGES).forEach(([lang, config]) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      const url = langHomeUrl(lang, config.path || '');
      link.setAttribute('href', url);
      link.setAttribute('data-dynamic-hreflang', 'true');
      head.appendChild(link);
    });

    // Open Graph locale + alternates
    const ogLocale = document.createElement('meta');
    ogLocale.setAttribute('property', 'og:locale');
    ogLocale.setAttribute('content', i18n.language.replace('-', '_'));
    ogLocale.setAttribute('data-dynamic-og', 'true');
    head.appendChild(ogLocale);

    Object.keys(SUPPORTED_LANGUAGES)
      .filter((l) => l !== i18n.language)
      .forEach((l) => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:locale:alternate');
        m.setAttribute('content', l.replace('-', '_'));
        m.setAttribute('data-dynamic-og', 'true');
        head.appendChild(m);
      });
  }, [i18n.language, window.location.pathname]);

  return null;
};